import { useCallback, useState } from 'react';
import { AxiosResponse } from 'axios';
import { toast } from 'sonner';

import { copyToClipboard } from '@/lib/copy-to-clipboard';
import { withShareUtm } from '@/lib/share-url';
import { useShowArticleSharingUrlById } from '@/hooks/api/article/use-article';

import { ShowArticleSharingUrlByIdRes } from './api/article/model';

export type ArticleShareArgs = {
  articleId?: number;
  shareUrl?: string | null;
};

export const useArticleShare = () => {
  const {
    mutateAsync: showArticleSharingUrlByIdAsync,
    mutate: showArticleSharingUrlById,
  } = useShowArticleSharingUrlById();

  const [isSharingUrlPending, setIsSharingUrlPending] = useState(false);
  const [isSharingUrlSuccess, setIsSharingUrlSuccess] = useState(false);
  const [isSharingUrlCopied, setIsSharingUrlCopied] = useState(false);

  const handleShare = useCallback(
    async ({ articleId, shareUrl }: ArticleShareArgs) => {
      if (!articleId || isSharingUrlPending) return;

      setIsSharingUrlPending(true);
      setIsSharingUrlSuccess(false);
      setIsSharingUrlCopied(false);

      let urlToCopy = shareUrl;

      try {
        const variables = {
          data: { id: articleId },
        };

        const onfulfilled = async (
          result: AxiosResponse<ShowArticleSharingUrlByIdRes, unknown, object>,
        ) => {
          if (result?.data?.shareUrl) {
            urlToCopy = result.data.shareUrl;
          }
        };

        if (urlToCopy) {
          showArticleSharingUrlById(variables, {
            onSuccess: onfulfilled,
          });
        } else {
          await showArticleSharingUrlByIdAsync(variables).then(onfulfilled);
        }

        if (!urlToCopy) {
          throw new Error('No url to copy available');
        }

        // Item 17: share attribution (see share-url.ts).
        const sharingResult = await copyToClipboard(
          withShareUtm(urlToCopy, 'article_analysis'),
        );
        if (sharingResult === 'cancelled') return;
        const isShareFlowSuccessful = Boolean(sharingResult);
        setIsSharingUrlSuccess(isShareFlowSuccessful);
        setIsSharingUrlCopied(
          sharingResult === 'clipboard' || sharingResult === 'exec-command',
        );

        if (sharingResult && sharingResult !== 'native-share') {
          toast.success('Sharing URL copied to clipboard', {
            duration: 4000,
          });
        }
      } catch (error) {
        setIsSharingUrlSuccess(false);
        setIsSharingUrlCopied(false);
        console.error('Error sharing article analysis:', error);
        toast.warning('Something went wrong while sharing the analysis URL', {
          duration: 4000,
        });
      } finally {
        setIsSharingUrlPending(false);
      }
    },
    [
      isSharingUrlPending,
      showArticleSharingUrlById,
      showArticleSharingUrlByIdAsync,
    ],
  );

  return {
    handleShare,
    isSharingUrlPending,
    isSharingUrlSuccess,
    isSharingUrlCopied,
    setIsSharingUrlCopied,
  };
};
